/**
 * Fiscal audit slice — random URSSAF / Customs audits triggered by high
 * notoriety. The probability ramps with notoriety, capped at 2 audits per
 * fiscal year.
 *
 * Resolution is deterministic from a compliance score that the player
 * influences via:
 *   - Premium insurance      → +20
 *   - Certification qualité  → +15 (inv-tech-04)
 *   - Admin/SAV support hired → +10/+5
 *   - Active restructuring resolution → −25 (cost-cutting raises flags)
 *   - High notoriety        → +5 if > 70 (notoriety also implies legitimacy)
 *
 * Outcomes:
 *   - score ≥ 70  → 'clean'      no fine, +2 reputation
 *   - score 40-70 → 'minor'      fine = 3 % of last-month revenue
 *   - score < 40  → 'major'      fine = 8 % of last-month revenue, -5 reputation
 */
export const createAuditSlice = (set, get) => ({
  pendingAudit: null,
  auditHistory: [],
  auditsThisYear: 0,
  lastAuditYear: 0,

  /**
   * Monthly audit roll — called once per month from coreSlice.nextDay after
   * the main commit. Probability ladder by notoriety:
   *   < 30 → 0 %
   *   30-60 → 1.5 %
   *   60-80 → 3 %
   *   > 80 → 5 %
   * Annual cap: 2 audits / fiscal year.
   */
  rollMonthlyAudit: () => {
    const state = get();
    if (!state.gameStarted) return { rolled: false };
    if (state.pendingAudit) return { rolled: false }; // one at a time
    const noto = state.notoriety || 0;
    const year = state.year || 1;
    // Reset annual counter at the start of a new fiscal year.
    let auditsThisYear = state.auditsThisYear || 0;
    let lastAuditYear = state.lastAuditYear || 0;
    if (lastAuditYear !== year) {
      auditsThisYear = 0;
      lastAuditYear = year;
    }
    if (auditsThisYear >= 2) return { rolled: false };

    let prob = 0;
    if (noto >= 80) prob = 0.05;
    else if (noto >= 60) prob = 0.03;
    else if (noto >= 30) prob = 0.015;

    if (Math.random() >= prob) {
      set({ auditsThisYear, lastAuditYear });
      return { rolled: false };
    }

    // Choose audit type — URSSAF (social charges) most of the time; Customs
    // unlocked only if the player has handled Export EU contracts.
    const hasExportHistory = (state.eventLog || []).some(e =>
      e?.message && /export|EU|douane/i.test(e.message)
    ) || (state.vehicles || []).some(v =>
      (v.specialties || []).some(s => /Export|International|Longue/i.test(s))
    );
    const auditType = hasExportHistory && Math.random() < 0.35 ? 'douanes' : 'urssaf';

    const pendingAudit = {
      id: `audit-${Date.now()}`,
      type: auditType,
      title: auditType === 'urssaf' ? 'Contrôle URSSAF' : 'Contrôle Douanes',
      reason: auditType === 'urssaf'
        ? 'Votre notoriété attire l\'attention de l\'URSSAF. Un contrôle des cotisations sociales et des contrats de travail est lancé.'
        : 'Vos opérations transfrontalières et votre notoriété déclenchent un contrôle douanier.',
      triggeredOn: state.totalDays || 0,
      year,
      month: state.month || 1,
      status: 'pending',
      lastMonthRevenue: Math.round(state.monthlyRevenues || 0),
    };

    set({
      pendingAudit,
      auditsThisYear: auditsThisYear + 1,
      lastAuditYear: year,
      notifications: [
        ...(state.notifications || []).slice(-30),
        {
          id: pendingAudit.id,
          type: 'audit',
          title: pendingAudit.title,
          message: pendingAudit.reason,
          day: state.totalDays || 0,
          read: false,
        },
      ],
    });
    return { rolled: true, type: auditType };
  },

  /**
   * Resolve the pending audit. Computes the compliance score from current
   * state and applies the outcome (fine + reputation delta).
   * Returns the resolved-audit record so the UI can show feedback.
   */
  resolveAudit: () => {
    const state = get();
    const audit = state.pendingAudit;
    if (!audit) return { ok: false, reason: 'no_pending' };

    let compliance = 50;
    // Insurance premium adds compliance.
    const premiumIns = (state.vehicles || []).some(v => v.insuranceCoverage === 'premium');
    if (premiumIns) compliance += 20;
    // Quality certification (inv-tech-04)
    if ((state.activeInvestments || {})['inv-tech-04']) compliance += 15;
    // Admin support hired (Rémi Forni)
    if ((state.hiredSupport || {})['role-christophe-faure']) compliance += 10;
    // SAV manager hired
    if ((state.hiredSupport || {})['role-lucas-fontaine']) compliance += 5;
    // Active restructuring → cost cutting raises red flags
    if (state.activeResolution?.type === 'restructuring' &&
        (state.totalDays || 0) < (state.activeResolution.expiresAt || 0)) {
      compliance -= 25;
    }
    // High notoriety can grant legitimacy (>70 → +5)
    if ((state.notoriety || 0) > 70) compliance += 5;

    // Douanes audits are slightly stricter (-5) — international operations
    // are subject to extra scrutiny.
    if (audit.type === 'douanes') compliance -= 5;

    const monthRev = audit.lastMonthRevenue || Math.round(state.monthlyRevenues || 0);
    let outcome, fine = 0, reputationDelta = 0;
    if (compliance >= 70) {
      outcome = 'clean';
      reputationDelta = 2;
    } else if (compliance >= 40) {
      outcome = 'minor';
      fine = Math.max(150, Math.round(monthRev * 0.03));
    } else {
      outcome = 'major';
      fine = Math.max(500, Math.round(monthRev * 0.08));
      reputationDelta = -5;
    }

    const resolved = {
      ...audit,
      status: 'resolved',
      resolvedOn: state.totalDays || 0,
      compliance,
      outcome,
      fine,
      reputationDelta,
    };

    set({
      pendingAudit: null,
      treasury: Math.round((state.treasury || 0) - fine),
      reputation: Math.max(0, Math.min(100, (state.reputation || 0) + reputationDelta)),
      auditHistory: [...(state.auditHistory || []), resolved].slice(-20),
      eventLog: [
        ...(state.eventLog || []),
        {
          id: resolved.id + '-resolved',
          day: state.totalDays || 0,
          title: resolved.title,
          type: outcome === 'clean' ? 'positive' : 'negative',
          message: outcome === 'clean'
            ? `Contrôle ${audit.type === 'urssaf' ? 'URSSAF' : 'douanier'} : conforme. +2 réputation.`
            : `Contrôle ${audit.type === 'urssaf' ? 'URSSAF' : 'douanier'} : redressement ${fine.toLocaleString('fr-FR')} €.`,
          impact: outcome === 'clean'
            ? { treasury: 0, reputation: 2 }
            : { treasury: -fine, reputation: reputationDelta },
        },
      ].slice(-100),
    });
    return { ok: true, outcome, fine, reputationDelta, compliance };
  },
});
